import {
  SpaceDocument,
  SpaceDocumentLimits,
  SpaceValidationError,
  SpaceValidationResult,
} from 'types/space';
import { LEGACY_BRIDGE_EVENT_TYPES, isLegacyBridgeEventType } from './legacyBridgeEventTypes';
import { DEFAULT_SPACE_DOCUMENT_LIMITS } from './limits';
import { walkComponentGraph } from './componentGraph';
import { findForbiddenPropKey } from './forbiddenPropKeys';

const SUPPORTED_FORMAT_VERSION = 'space/v1';
const SUPPORTED_A2UI_VERSION = 'v1.0';

/**
 * Validates a Space document envelope: `formatVersion`, `a2ui` (v1.0 payload, resolvable `root`,
 * unique component ids), and the security/durability limits (component count, payload size).
 * Fail-closed: any structural ambiguity is an error, never a best-effort pass.
 */
export function validateSpaceDocument(
  doc: unknown,
  limits: Partial<SpaceDocumentLimits> = {}
): SpaceValidationResult {
  const effectiveLimits: SpaceDocumentLimits = { ...DEFAULT_SPACE_DOCUMENT_LIMITS, ...limits };
  const errors: SpaceValidationError[] = [];

  if (!doc || typeof doc !== 'object') {
    return {
      valid: false,
      errors: [{ code: 'MISSING_FORMAT_VERSION', message: 'Space document must be an object' }],
    };
  }

  const candidate = doc as Partial<SpaceDocument>;

  if (!candidate.formatVersion) {
    errors.push({
      code: 'MISSING_FORMAT_VERSION',
      message: 'Space document is missing required field "formatVersion"',
      path: 'formatVersion',
    });
  } else if (candidate.formatVersion !== SUPPORTED_FORMAT_VERSION) {
    errors.push({
      code: 'UNSUPPORTED_FORMAT_VERSION',
      message: `Unsupported formatVersion "${candidate.formatVersion}"; expected "${SUPPORTED_FORMAT_VERSION}"`,
      path: 'formatVersion',
    });
  }

  if (!candidate.surface) {
    errors.push({
      code: 'MISSING_SURFACE',
      message: 'Space document is missing required field "surface"',
      path: 'surface',
    });
  }

  if (!candidate.a2ui) {
    errors.push({
      code: 'MISSING_A2UI',
      message: 'Space document is missing required field "a2ui"',
      path: 'a2ui',
    });
    return { valid: false, errors };
  }

  const { a2ui } = candidate;

  if (a2ui.version !== SUPPORTED_A2UI_VERSION) {
    errors.push({
      code: 'INVALID_A2UI_VERSION',
      message: `Unsupported a2ui.version "${a2ui.version}"; expected "${SUPPORTED_A2UI_VERSION}"`,
      path: 'a2ui/version',
    });
  }

  if (!Array.isArray(a2ui.components)) {
    errors.push({
      code: 'MISSING_COMPONENTS',
      message: 'a2ui.components is required and must be an array',
      path: 'a2ui/components',
    });
    return { valid: false, errors };
  }

  if (a2ui.components.length > effectiveLimits.maxComponents) {
    errors.push({
      code: 'COMPONENT_COUNT_EXCEEDS_LIMIT',
      message: `a2ui.components exceeds maximum of ${effectiveLimits.maxComponents} entries (received ${a2ui.components.length})`,
      path: 'a2ui/components',
    });
  }

  const payloadSize = Buffer.byteLength(JSON.stringify(a2ui.components), 'utf-8');
  if (payloadSize > effectiveLimits.maxPayloadBytes) {
    errors.push({
      code: 'PAYLOAD_SIZE_EXCEEDS_LIMIT',
      message: `a2ui.components payload exceeds maximum of ${Math.round(effectiveLimits.maxPayloadBytes / 1024)}KB (received ${Math.round(payloadSize / 1024)}KB)`,
      path: 'a2ui/components',
    });
  }

  const seenIds = new Set<string>();
  a2ui.components.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || !entry.id) {
      errors.push({
        code: 'MISSING_COMPONENTS',
        message: `a2ui.components[${index}] is missing a valid "id"`,
        path: `a2ui/components/${index}`,
      });
      return;
    }

    if (seenIds.has(entry.id)) {
      errors.push({
        code: 'DUPLICATE_COMPONENT_ID',
        message: `Duplicate component id "${entry.id}" at a2ui.components[${index}]`,
        path: `a2ui/components/${index}`,
      });
    }
    seenIds.add(entry.id);

    // A2UI v1.0 lets a component opt into shipping the whole data model with its actions
    // (`sendDataModel`). Center does not implement that opt-in — action context is explicitly
    // bound, never an implicit full-model dump. Rejecting the key is the difference between a
    // documented guarantee and a prop that is quietly ignored while an author believes it works.
    if (entry.sendDataModel !== undefined) {
      errors.push({
        code: 'SEND_DATA_MODEL_NOT_SUPPORTED',
        message: `Component "${entry.id}" declares "sendDataModel"; Center requires action context to be explicitly bound (e.g. Button.actionContext)`,
        path: `a2ui/components/${index}/sendDataModel`,
      });
    }

    const forbiddenKeyPath = findForbiddenPropKey(entry);
    if (forbiddenKeyPath) {
      errors.push({
        code: 'FORBIDDEN_PROP_KEY',
        message: `Component "${entry.id}" props contain a forbidden key at "${forbiddenKeyPath}"`,
        path: `a2ui/components/${index}/${forbiddenKeyPath}`,
      });
    }
  });

  if (!a2ui.root) {
    errors.push({
      code: 'MISSING_ROOT',
      message: 'a2ui.root is required and must reference a component id in a2ui.components',
      path: 'a2ui/root',
    });
  } else if (!seenIds.has(a2ui.root)) {
    errors.push({
      code: 'ROOT_NOT_FOUND',
      message: `a2ui.root "${a2ui.root}" does not match any entry in a2ui.components`,
      path: 'a2ui/root',
    });
  } else {
    const issue = walkComponentGraph(a2ui.components, a2ui.root, effectiveLimits.maxNestingDepth);
    if (issue?.type === 'CYCLE') {
      errors.push({
        code: 'CYCLE_DETECTED',
        message: `Cyclic component reference detected: ${issue.path.join(' -> ')}`,
        path: `a2ui/components/${issue.path[0]}`,
      });
    } else if (issue?.type === 'DEPTH_EXCEEDED') {
      errors.push({
        code: 'NESTING_DEPTH_EXCEEDS_LIMIT',
        message: `Component tree exceeds maximum nesting depth of ${effectiveLimits.maxNestingDepth} (path: ${issue.path.join(' -> ')})`,
        path: `a2ui/components/${issue.path[0]}`,
      });
    }
  }

  if (Array.isArray(candidate.wires)) {
    candidate.wires.forEach((wire, index) => {
      if (!wire || typeof wire !== 'object') return;
      (['from', 'to'] as const).forEach((field) => {
        const targetId = wire[field];
        if (typeof targetId === 'string' && !seenIds.has(targetId)) {
          errors.push({
            code: 'WIRE_TARGET_NOT_FOUND',
            message: `Wire "${wire.id ?? index}" references unknown component "${targetId}" via "${field}"`,
            path: `wires/${index}/${field}`,
          });
        }
      });

      // A `legacyEventType` outside the closed bridge set can never be delivered — the renderer
      // drops it. Rejecting it here makes that an authoring-time error instead of a wire that
      // looks connected in the document and silently does nothing at runtime.
      if (wire.legacyEventType !== undefined && !isLegacyBridgeEventType(wire.legacyEventType)) {
        errors.push({
          code: 'WIRE_LEGACY_EVENT_TYPE_NOT_ALLOWED',
          message: `Wire "${wire.id ?? index}" declares legacyEventType "${String(
            wire.legacyEventType
          )}", which is not one of: ${LEGACY_BRIDGE_EVENT_TYPES.join(', ')}`,
          path: `wires/${index}/legacyEventType`,
        });
      }
    });
  }

  return { valid: errors.length === 0, errors };
}
